import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Button,
  Caption1,
  InfoBox,
  Label,
  List,
  ListItem,
  Menu,
  Select,
  SelectTrigger,
  Textarea,
} from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { SUGGESTION } from "~/components/tasks/SuggestWizard/suggestionUtils";
import { showErrorToast } from "~/components/Toast";
import { ProjectForm } from "~/pages/GettingStarted/ProjectForm";
import StepLoader from "~/pages/GettingStarted/StepLoader";
import { useAvailableAiModels } from "~/repository/useAi";
import { useProjectPromptTemplates } from "~/repository/usePredefinedProjectTemplates";
import { useSuggestProject } from "~/repository/useSuggestProject";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";

export const ProjectPrompt = ({ onClose, isAlreadyCompleted, setProjectId }) => {
  const { t } = useTranslation();
  const promptRef = useRef(null);
  const [prompt, setPrompt] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState(null);
  const [aiModel, setAiModel] = useState(null);
  const switchAiModelFF = useSelector((state) => getFeatureFlag(state, "switch_ai_model"));
  const aiEnabledFF = useSelector((state) => getFeatureFlag(state, "ai_enabled"));
  const showAiModelSelect = useMemo(() => switchAiModelFF && aiEnabledFF, [aiEnabledFF, switchAiModelFF]);
  const { mutateAsync, isLoading: isLoadingSuggestions } = useSuggestProject();
  const { data: aiModelsResponse, isFetched: isAiModelsFetched } = useAvailableAiModels({
    onSuccess: (aiModels) => setAiModel(`${aiModels[0].short_name}/fast`),
    enabled: showAiModelSelect,
  });
  const { data: templates, isLoading: isLoadingTemplates } = useProjectPromptTemplates({
    onSuccess: (templates) => {
      setPrompt(templates[0].description);
      setSelectedTemplate(templates[0].name);
    },
  });

  const aiModels = useMemo(() => {
    if (isAiModelsFetched) {
      const models = [];
      aiModelsResponse.forEach((aiModel) => {
        models.push({ id: `${aiModel.short_name}/fast`, name: aiModel.messaging.fast.name });
        models.push({ id: `${aiModel.short_name}/precise`, name: aiModel.messaging.precise.name });
      });

      return models;
    }

    return [];
  }, [aiModelsResponse, isAiModelsFetched]);

  const onClick = useCallback(() => {
    const data = { message: prompt };

    if (showAiModelSelect) {
      const [model, mode] = aiModel.split("/");
      data.model = model;
      data.mode = mode;
    }

    mutateAsync(data)
      .then((res) => {
        if (res?.suggested_project?.name) {
          const { name, task_lists } = res.suggested_project;
          setFormData({
            name,
            task_lists: task_lists.map((tl) => {
              return {
                ...tl,
                id: v4(),
                tasks: tl.tasks.map((t) => {
                  return {
                    ...t,
                    id: v4(),
                    variant: SUGGESTION,
                    estimate: `${t.estimate}`,
                  };
                }),
              };
            }),
          });
          return setShowForm(true);
        }

        showErrorToast("There was an error while loading suggestions.");
      })
      .catch(() => {
        showErrorToast("There was an error while loading suggestions.");
      });
  }, [aiModel, mutateAsync, prompt, showAiModelSelect]);

  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return isLoadingSuggestions || isLoadingTemplates ? (
    <StepLoader />
  ) : showForm ? (
    <div className="tw-pb-4">
      <ProjectForm
        projectName={formData?.name}
        taskLists={formData?.task_lists}
        setFormState={() => {}}
        onClose={onClose}
        isAlreadyCompleted={isAlreadyCompleted}
        setProjectId={setProjectId}
      />
    </div>
  ) : (
    <>
      <InfoBox type="default">
        {t(
          "Write a short project description or use one of our prompts, and we’ll come up with a project name, a task list, and assignments for you."
        )}
      </InfoBox>
      {showAiModelSelect ? (
        <div className="tw-flex tw-flex-col">
          <Label
            size="small"
            htmlFor="ai-model"
            className="tw-mb-1"
          >
            {t("Model")}
          </Label>
          <Select
            id="ai-model"
            options={aiModels}
            selected={aiModel}
            target={<SelectTrigger className="tw-w-48">{aiModels.find((m) => m.id === aiModel)?.name}</SelectTrigger>}
            onChange={setAiModel}
            noResultText={t("No result")}
            forceCloseMenu
            disableSearch
          />
        </div>
      ) : null}
      <div className="tw-flex tw-flex-col">
        <Label
          size="small"
          htmlFor="project-description"
          className="tw-mb-1"
        >
          {t("Project Description")}
        </Label>
        <Textarea
          id="project-description"
          autoFocus
          onChange={(e) => setPrompt(e.target.value)}
          className="tw-w-full tw-resize-none"
          value={prompt}
          rows={6}
          ref={promptRef}
        />
      </div>
      <div className="tw-flex tw-flex-col">
        <Label
          size="small"
          htmlFor="project-templates"
          className="tw-mb-1"
        >
          {t("Templates")}
        </Label>
        {templates ? (
          <div className="tw-flex tw-gap-2 tw-flex-wrap">
            {templates
              .filter((t, i) => i < 5)
              .map(({ name, description }) => {
                return (
                  <SelectTrigger
                    key={name}
                    className={classNames("tw-w-fit", {
                      "tw-border-[var(--color-primary-700)]": name === selectedTemplate,
                    })}
                    size="small"
                    endAdornment={<></>}
                    onClick={() => {
                      setPrompt(description);
                      setSelectedTemplate(name);
                      promptRef?.current?.focus();
                    }}
                  >
                    <Caption1 color={name === selectedTemplate ? "theme-primary" : "primary"}>{name}</Caption1>
                  </SelectTrigger>
                );
              })}
            {templates.length > 5 ? (
              <Menu
                onOpen={openMenu}
                onClose={closeMenu}
                open={menuOpen}
                target={
                  <SelectTrigger
                    key={name}
                    className="tw-w-fit"
                    size="small"
                  >
                    <Caption1
                      color={
                        templates
                          .filter((t, i) => i > 5)
                          .map((t) => t.name)
                          .includes(selectedTemplate)
                          ? "theme-primary"
                          : "primary"
                      }
                    >
                      {t("More Templates")}
                    </Caption1>
                  </SelectTrigger>
                }
              >
                <List>
                  {templates
                    .filter((t, i) => i > 5)
                    .map(({ name, description }) => {
                      return (
                        <ListItem
                          key={name}
                          onClick={() => {
                            setPrompt(description);
                            setMenuOpen(false);
                            setSelectedTemplate(name);
                            promptRef?.current?.focus();
                          }}
                        >
                          {name}
                        </ListItem>
                      );
                    })}
                </List>
              </Menu>
            ) : null}
          </div>
        ) : null}
      </div>
      <Button
        onClick={onClick}
        className="tw-w-fit"
      >
        {t("Suggest Tasks")}
      </Button>
    </>
  );
};

ProjectPrompt.propTypes = {
  onClose: PropTypes.func.isRequired,
  isAlreadyCompleted: PropTypes.bool,
  setProjectId: PropTypes.func,
};
